// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback } from 'react';
import { ProviderService } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Service/Provider';
import { useParties } from '../../../contexts/PartiesContext';
import { useCreateDelegatedBatchedMarkersProxy } from '../../../hooks/mutations/commercialAgreements/useCreateDelegatedBatchedMarkersProxy';
import { useHasDelegatedBatchedMarkersProxy } from '../../../hooks/queries/commercialAgreements/useHasDelegatedBatchedMarkersProxy';
import useIsPackageAvailableFrom from '../../../hooks/queries/settings/useIsPackageAvailableFrom';
import TemplateContract from '../../../utils/TemplateContract';

type UseCreateDelegatedBatchedMarkersProxyActionParams = {
  providerServiceData?: TemplateContract<ProviderService> | null;
};

type UseCreateDelegatedBatchedMarkersProxyActionResult = {
  showCreateDelegatedBatchedMarkersProxyButton: boolean;
  canCreateDelegatedBatchedMarkersProxy: boolean;
  onCreateDelegatedBatchedMarkersProxy: () => void;
  isCreateDelegatedBatchedMarkersProxyLoading: boolean;
};

export const useCreateDelegatedBatchedMarkersProxyAction = ({
  providerServiceData,
}: UseCreateDelegatedBatchedMarkersProxyActionParams): UseCreateDelegatedBatchedMarkersProxyActionResult => {
  const { party, operator } = useParties();
  const createDelegatedBatchedMarkersProxy = useCreateDelegatedBatchedMarkersProxy();
  const providerServiceOperator = providerServiceData?.payload.operator;
  const providerServiceProvider = providerServiceData?.payload.provider;
  const isProvider = providerServiceProvider === party;

  const enableCreateDelegatedBatchedMarkersProxy = useIsPackageAvailableFrom(
    'utility-commercials-v0',
    [party, operator],
    '0.4.1',
    isProvider,
  );

  const showCreateDelegatedBatchedMarkersProxyButton =
    isProvider && !!enableCreateDelegatedBatchedMarkersProxy.data;

  const hasDelegatedBatchedMarkersProxy = useHasDelegatedBatchedMarkersProxy(
    providerServiceOperator,
    providerServiceProvider,
  );

  const canCreateDelegatedBatchedMarkersProxy =
    !!providerServiceOperator &&
    !!providerServiceProvider &&
    !!enableCreateDelegatedBatchedMarkersProxy.data &&
    !hasDelegatedBatchedMarkersProxy.data;

  const onCreateDelegatedBatchedMarkersProxy = useCallback(() => {
    if (!providerServiceOperator || !providerServiceProvider) {
      throw new Error(
        'Cannot create delegated batched markers proxy: missing operator or provider',
      );
    }
    createDelegatedBatchedMarkersProxy({
      argument: {
        operator: providerServiceOperator,
        provider: providerServiceProvider,
      },
    }).catch(() => undefined);
  }, [createDelegatedBatchedMarkersProxy, providerServiceOperator, providerServiceProvider]);

  return {
    showCreateDelegatedBatchedMarkersProxyButton,
    canCreateDelegatedBatchedMarkersProxy,
    onCreateDelegatedBatchedMarkersProxy,
    isCreateDelegatedBatchedMarkersProxyLoading:
      isProvider &&
      (hasDelegatedBatchedMarkersProxy.isLoading ||
        enableCreateDelegatedBatchedMarkersProxy.isLoading),
  };
};
